import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import { collection, doc, orderBy, query, updateDoc, where, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../../firebase';
import LoadingLockScreen from '../LoadingLockScreen';
import TeamMemberProfileEditor from '../TeamMemberProfileEditor';
import RolePermissionsMatrix from './RolePermissionsMatrix';
import { appendTicketLog } from '../../lib/ticketLogs';
import { useCompanyUsers } from '../../hooks/useCompanyUsers';
import { useFirestoreQuery } from '../../hooks/useFirestoreQuery';
import { useWorkspace } from '../../context/WorkspaceContext';
import {
  MASTER_MODULE_KEYS,
  normalizeRolePermissions,
  resolveMasterActiveModules,
} from '../../lib/rolePermissions';
import {
  canAssignUserRole,
  canCreateUsers,
  canEditRolePermissions,
  canManageTargetUser,
  getAssignableRoles,
  getEditablePermissionRoles,
  ROLE_LABELS,
  roleRank,
} from '../../lib/userRoleHierarchy';

function displayLabel(u) {
  return u.displayName || u.fullName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u.uid;
}

function getAssignedIds(link) {
  return Array.isArray(link.assignedUsers) ? link.assignedUsers : [];
}

function countLinksForUser(links, uid) {
  return links.filter((l) => getAssignedIds(l).includes(uid)).length;
}

function canEditTeamMemberProfile(editorRole, editorUid, targetUser) {
  if (!targetUser || !editorUid || targetUser.uid === editorUid) return false;
  if (!['company_admin', 'developer'].includes(editorRole)) return false;
  return roleRank(editorRole) > roleRank(targetUser.role);
}

/**
 * Tenant Users hub — create users, assign roles (hierarchy), configure RBAC matrix.
 */
export default function UsersManagementHub({ userData }) {
  const companyId = userData?.companyId;
  const actorRole = userData?.role;
  const editorUid = auth.currentUser?.uid;
  const isCompanyAdmin = actorRole === 'company_admin';
  const assignableRoles = useMemo(() => getAssignableRoles(actorRole), [actorRole]);
  const editablePermRoles = useMemo(() => getEditablePermissionRoles(actorRole), [actorRole]);

  const { workspace } = useWorkspace();

  const [sectionTab, setSectionTab] = useState('people');
  const [loading, setLoading] = useState(true);
  const [splashOnce, setSplashOnce] = useState(true);
  const [linksError, setLinksError] = useState(null);
  const [error, setError] = useState(null);
  const [rowSaving, setRowSaving] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState(() => assignableRoles[assignableRoles.length - 1] || 'agent');
  const [formError, setFormError] = useState('');

  const [accessDialog, setAccessDialog] = useState({
    open: false,
    user: null,
    initialLinkIds: [],
    selectedLinkIds: [],
  });
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [profileUser, setProfileUser] = useState(null);

  const [rolePermissions, setRolePermissions] = useState(() => normalizeRolePermissions(null));
  const [permissionsDirty, setPermissionsDirty] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [permissionsMsg, setPermissionsMsg] = useState('');
  const [permissionsError, setPermissionsError] = useState('');

  const showLinksCols = isCompanyAdmin;
  const showProfileCol = isCompanyAdmin || actorRole === 'developer';
  const tableColSpan = 4 + (showLinksCols ? 2 : 0) + (showProfileCol ? 1 : 0);

  const { users: allUsers, loading: usersLoading } = useCompanyUsers(companyId);

  const linksQuery = useMemo(() => {
    if (!companyId || !isCompanyAdmin) return null;
    return query(
      collection(db, 'links'),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc'),
    );
  }, [companyId, isCompanyAdmin]);

  const {
    data: links,
    loading: linksLoading,
    error: linksQueryError,
  } = useFirestoreQuery(linksQuery, { enabled: Boolean(companyId && isCompanyAdmin) });

  useEffect(() => {
    if (linksQueryError) {
      const msg = linksQueryError.message || '';
      if (msg.includes('index') || msg.includes('building')) {
        setLinksError('Could not load external links (index may still be building).');
      } else {
        setLinksError(msg);
      }
    } else {
      setLinksError(null);
    }
  }, [linksQueryError]);

  useEffect(() => {
    setRolePermissions(normalizeRolePermissions(workspace?.rolePermissions));
    setPermissionsDirty(false);
  }, [workspace?.rolePermissions, workspace?.id]);

  const masterActiveModules = useMemo(() => resolveMasterActiveModules(workspace), [workspace]);
  const activeMasterModuleKeys = useMemo(
    () => MASTER_MODULE_KEYS.filter((key) => masterActiveModules[key]),
    [masterActiveModules],
  );

  const directory = useMemo(() => {
    const sorted = [...allUsers].sort((a, b) => {
      const ao = roleRank(a.role);
      const bo = roleRank(b.role);
      if (ao !== bo) return bo - ao;
      return displayLabel(a).localeCompare(displayLabel(b));
    });
    return sorted;
  }, [allUsers]);

  useEffect(() => {
    const done = !usersLoading && (!isCompanyAdmin || !linksLoading);
    setLoading(!done);
    if (done) setSplashOnce(false);
  }, [usersLoading, linksLoading, isCompanyAdmin]);

  const openCreate = () => {
    setFormName('');
    setFormEmail('');
    setFormPassword('');
    setFormRole(assignableRoles[assignableRoles.length - 1] || 'agent');
    setFormError('');
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!formEmail.trim() || !formPassword) {
      setFormError('Email and password are required.');
      return;
    }
    setCreating(true);
    setFormError('');
    try {
      const createUser = httpsCallable(functions, 'createUser');
      await createUser({
        email: formEmail.trim(),
        password: formPassword,
        name: formName.trim(),
        role: formRole,
        companyId,
      });
      const actor = auth.currentUser;
      if (actor && companyId) {
        await appendTicketLog(db, {
          companyId,
          ticketId: null,
          action: 'team_member_created',
          summary: `User created: ${formEmail.trim()} (${formRole})`,
          meta: { email: formEmail.trim(), role: formRole },
          actorUid: actor.uid,
          actorEmail: actor.email || '',
          actorRole: actorRole || '',
          participantUids: [actor.uid],
          resourceType: 'team',
        });
      }
      setDialogOpen(false);
    } catch (err) {
      console.error('createUser:', err);
      const msg = err.message || '';
      if (err.code === 'functions/already-exists' || msg.includes('already')) {
        setFormError('That email is already in use.');
      } else {
        setFormError(msg || 'Could not create user.');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (targetUser, newRole) => {
    if (!canAssignUserRole(actorRole, targetUser.role, newRole)) return;
    setRowSaving(targetUser.uid);
    setError(null);
    try {
      const updateUserRole = httpsCallable(functions, 'updateUserRole');
      await updateUserRole({ targetUid: targetUser.uid, newRole });
      const actor = auth.currentUser;
      if (actor && companyId) {
        await appendTicketLog(db, {
          companyId,
          ticketId: null,
          action: 'team_role_changed',
          summary: `User role set to ${newRole}`,
          meta: { targetUid: targetUser.uid, newRole },
          actorUid: actor.uid,
          actorEmail: actor.email || '',
          actorRole: actorRole || '',
          participantUids: [actor.uid, targetUser.uid].filter(Boolean),
          resourceType: 'team',
        });
      }
    } catch (err) {
      console.error('updateUserRole:', err);
      setError(err.message || 'Could not update role.');
    } finally {
      setRowSaving(null);
    }
  };

  const handleSavePermissions = async () => {
    if (!companyId) return;
    setSavingPermissions(true);
    setPermissionsError('');
    setPermissionsMsg('');
    try {
      const payload = {};
      editablePermRoles.forEach((role) => {
        payload[role] = rolePermissions[role] || {};
      });

      if (isCompanyAdmin) {
        await updateDoc(doc(db, 'companies', companyId), {
          rolePermissions: normalizeRolePermissions(rolePermissions),
        });
      } else {
        const fn = httpsCallable(functions, 'updateRolePermissions');
        await fn({ companyId, rolePermissions: payload });
      }
      setPermissionsDirty(false);
      setPermissionsMsg('Role permissions saved.');
    } catch (e) {
      console.error('save permissions:', e);
      setPermissionsError(e.message || 'Could not save role permissions.');
    } finally {
      setSavingPermissions(false);
    }
  };

  const openLinkAccess = (user) => {
    const selected = links.filter((l) => getAssignedIds(l).includes(user.uid)).map((l) => l.id);
    setAccessError('');
    setAccessDialog({
      open: true,
      user,
      initialLinkIds: [...selected],
      selectedLinkIds: [...selected],
    });
  };

  const closeLinkAccess = () => {
    if (accessSaving) return;
    setAccessDialog({ open: false, user: null, initialLinkIds: [], selectedLinkIds: [] });
  };

  const toggleLinkForAccess = (linkId) => {
    setAccessDialog((d) => {
      const has = d.selectedLinkIds.includes(linkId);
      return {
        ...d,
        selectedLinkIds: has ? d.selectedLinkIds.filter((id) => id !== linkId) : [...d.selectedLinkIds, linkId],
      };
    });
  };

  const handleSaveLinkAccess = async () => {
    if (!accessDialog.user || !isCompanyAdmin) return;
    const uid = accessDialog.user.uid;
    const initial = new Set(accessDialog.initialLinkIds);
    const selected = new Set(accessDialog.selectedLinkIds);

    setAccessSaving(true);
    setAccessError('');
    try {
      const batch = writeBatch(db);
      let ops = 0;
      links.forEach((link) => {
        const had = initial.has(link.id);
        const wants = selected.has(link.id);
        if (had === wants) return;
        const next = new Set(getAssignedIds(link));
        if (wants) next.add(uid);
        else next.delete(uid);
        batch.update(doc(db, 'links', link.id), { assignedUsers: Array.from(next) });
        ops += 1;
      });
      if (ops > 0) await batch.commit();
      closeLinkAccess();
    } catch (err) {
      console.error('saveLinkAccess:', err);
      setAccessError(err.message || 'Could not update link assignments.');
    } finally {
      setAccessSaving(false);
    }
  };

  if (!companyId) {
    return (
      <Alert severity="info">
        Your account is not linked to a company yet. User management is unavailable.
      </Alert>
    );
  }

  if (loading && splashOnce) {
    return <LoadingLockScreen open />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hierarchyHint = {
    company_admin: 'Create developers and staff, assign roles, and configure all role permissions.',
    developer: 'Create managers and agents below you. Edit permissions for Manager and Agent roles.',
    manager: 'Create agents and trainees. Edit Agent role permissions only.',
  }[actorRole] || '';

  return (
    <Box>
      <LoadingLockScreen open={creating || accessSaving} />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Users
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
            {hierarchyHint}
            {' '}
            You can only manage users and roles strictly below your level in the hierarchy.
          </Typography>
        </Box>
        {canCreateUsers(actorRole) && (
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={openCreate}>
            Add user
          </Button>
        )}
      </Stack>

      <Tabs value={sectionTab} onChange={(_, v) => setSectionTab(v)} sx={{ mb: 3 }}>
        <Tab value="people" label="People & roles" />
        {canEditRolePermissions(actorRole) && (
          <Tab value="permissions" label="Role permissions" icon={<SecurityIcon fontSize="small" />} iconPosition="start" />
        )}
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {linksError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setLinksError(null)}>
          {linksError}
        </Alert>
      )}

      {sectionTab === 'people' && (
        <>
          {isCompanyAdmin && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Company admins see every external link. Assign links per user so agents only see what they need.
            </Typography>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={56} />
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell width={180}>Role</TableCell>
                  {showLinksCols && <TableCell width={100}>Links</TableCell>}
                  {showLinksCols && <TableCell width={160}>External links</TableCell>}
                  {showProfileCol && <TableCell width={72} align="right">Profile</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {directory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No users in this company yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  directory.map((u) => {
                    const manageable = canManageTargetUser(actorRole, u.role);
                    const roleOptions = manageable
                      ? assignableRoles.filter((r) => canAssignUserRole(actorRole, u.role, r))
                      : [];
                    const nLinks = isCompanyAdmin ? countLinksForUser(links, u.uid) : 0;
                    const canEditProfile = canEditTeamMemberProfile(actorRole, editorUid, u);
                    return (
                      <TableRow key={u.uid} hover>
                        <TableCell>
                          <Avatar src={u.avatarUrl || ''} sx={{ width: 36, height: 36 }}>
                            {displayLabel(u).charAt(0).toUpperCase()}
                          </Avatar>
                        </TableCell>
                        <TableCell>{displayLabel(u)}</TableCell>
                        <TableCell>{u.email || '—'}</TableCell>
                        <TableCell>
                          {roleOptions.length > 0 ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <FormControl size="small" sx={{ minWidth: 140 }} disabled={rowSaving === u.uid}>
                                <InputLabel id={`role-${u.uid}`}>Role</InputLabel>
                                <Select
                                  labelId={`role-${u.uid}`}
                                  label="Role"
                                  value={u.role}
                                  onChange={(e) => handleRoleChange(u, e.target.value)}
                                >
                                  {roleOptions.map((r) => (
                                    <MenuItem key={r} value={r}>
                                      {ROLE_LABELS[r] || r}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              {rowSaving === u.uid && <CircularProgress size={22} />}
                            </Stack>
                          ) : (
                            <Chip
                              size="small"
                              label={ROLE_LABELS[u.role] || u.role}
                              color={u.role === 'company_admin' ? 'secondary' : 'default'}
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        {showLinksCols && (
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {nLinks} / {links.length}
                            </Typography>
                          </TableCell>
                        )}
                        {showLinksCols && (
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LinkIcon />}
                              onClick={() => openLinkAccess(u)}
                              disabled={links.length === 0}
                            >
                              Manage
                            </Button>
                          </TableCell>
                        )}
                        {showProfileCol && (
                          <TableCell align="right">
                            {canEditProfile ? (
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label="Edit profile"
                                onClick={() => setProfileUser(u)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {sectionTab === 'permissions' && canEditRolePermissions(actorRole) && (
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Role Permissions Matrix
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Turn core modules on or off for Manager, Developer, and Agent roles. Company admins always have full access to active modules.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={savingPermissions ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSavePermissions}
                disabled={!permissionsDirty || savingPermissions}
              >
                Save permissions
              </Button>
            </Stack>

            {permissionsError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPermissionsError('')}>
                {permissionsError}
              </Alert>
            )}
            {permissionsMsg && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPermissionsMsg('')}>
                {permissionsMsg}
              </Alert>
            )}

            <RolePermissionsMatrix
              rolePermissions={rolePermissions}
              onChange={(next) => {
                setRolePermissions(next);
                setPermissionsDirty(true);
                setPermissionsMsg('');
                setPermissionsError('');
              }}
              editableRoles={editablePermRoles}
              activeModuleKeys={activeMasterModuleKeys}
              disabled={savingPermissions}
            />
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onClose={() => !creating && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add user</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField label="Name (optional)" fullWidth value={formName} onChange={(e) => setFormName(e.target.value)} />
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
            />
            <TextField
              label="Temporary password"
              type="password"
              required
              fullWidth
              value={formPassword}
              onChange={(e) => setFormPassword(e.target.value)}
              helperText="Share securely; they should change it after first login."
            />
            <FormControl fullWidth>
              <InputLabel id="new-user-role-label">Role</InputLabel>
              <Select
                labelId="new-user-role-label"
                label="Role"
                value={formRole}
                onChange={(e) => setFormRole(e.target.value)}
              >
                {assignableRoles.map((r) => (
                  <MenuItem key={r} value={r}>
                    {ROLE_LABELS[r] || r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={accessDialog.open} onClose={closeLinkAccess} fullWidth maxWidth="sm">
        <DialogTitle>
          External links for {accessDialog.user ? displayLabel(accessDialog.user) : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {accessError && <Alert severity="error">{accessError}</Alert>}
            {links.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No links yet. Add some under External Links.
              </Typography>
            ) : (
              links.map((link) => {
                const checked = accessDialog.selectedLinkIds.includes(link.id);
                return (
                  <Stack
                    key={link.id}
                    direction="row"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleLinkForAccess(link.id)}
                      disabled={accessSaving}
                    />
                    <ListItemText
                      disableTypography
                      primary={<Typography variant="subtitle2">{link.title}</Typography>}
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {(link.category || 'Uncategorized') + ' · ' + (link.url || '')}
                        </Typography>
                      }
                    />
                  </Stack>
                );
              })
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLinkAccess} disabled={accessSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveLinkAccess} disabled={accessSaving || links.length === 0}>
            {accessSaving ? 'Saving…' : 'Save assignments'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(profileUser)} onClose={() => setProfileUser(null)} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle>Edit directory profile — {profileUser ? displayLabel(profileUser) : ''}</DialogTitle>
        <DialogContent dividers>
          {profileUser && (
            <TeamMemberProfileEditor
              targetUid={profileUser.uid}
              onSaved={() => {}}
              auditLog={companyId ? { companyId, actorRole: actorRole || '' } : null}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileUser(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
